<template>
  <a-modal
    v-if="visible"
    title="Move Shifts"
    :visible="visible"
    :destroy-on-close="true"
    width="600px"
    data-testid="shifts-move-shifts-modal"
    @cancel="closeModal"
  >
    <a-form-model
      layout="horizontal"
      :model="form"
      :rules="rules"
      ref="formRef"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <a-form-model-item :colon="false" label-align="left" label="Holder" prop="holder_id">
        <search-associates :value.sync="form.holder_id" />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Assignee" prop="assignee_id">
        <a-select
          v-model="form.assignee_id"
          :dropdown-match-select-width="false"
          :allow-clear="true"
          :disabled="!form.holder_id"
          :placeholder="loadingProfiles ? 'Loading...' : 'Assignee'"
          :loading="loadingProfiles"
          option-label-prop="children"
        >
          <template #notFoundContent>
            <span class="text-center">
              The associate does not have any other profiles <br />
              to move shifts to</span
            ></template
          >
          <a-select-option
            v-for="profile in profiles.filter(item => item.associate_number !== form.holder_id)"
            :key="profile.id"
            :value="profile.id"
          >
            <!-- <a-tooltip placement="left">
              <template #title>
                <span>{{
                  getTagStatus(profile.work_status, profile.termination?.type)
                    ?.title
                }}</span>
              </template>
              <a-badge
                :color="
                  getTagStatus(profile.work_status, profile.termination?.type)
                    ?.color
                "
              />
            </a-tooltip> -->
            {{ `[${profile.associate_number}] ${profile.first_name} ${profile.last_name}` }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <template v-if="form.holder_id"
        ><a-form-model-item :colon="false" label-align="left" label="Date Range">
          <a-range-picker
            class="w-full"
            format="MM/DD/YYYY"
            v-model="dateProxy"
            valueFormat="YYYY-MM-DD"
            :ranges="dateRange"
          />
        </a-form-model-item>
        <a-form-model-item :colon="false" label-align="left" label="Status">
          <custom-select
            :multiple="false"
            :value.sync="filters.shift_statuses"
            placeholder="Status"
            :allow-clear="false"
            :options="shiftStatuses"
            name="title"
            optionKey="key"
          />
        </a-form-model-item>

        <a-table
          :row-selection="{
            selectedRowKeys: form.shift_ids,
            onChange: onSelectChange,
            getCheckboxProps: record => ({
              props: { disabled: record.status === 'deleted' }
            })
          }"
          :rowKey="record => record.id"
          :locale="{ emptyText: 'Shifts Not Found' }"
          :loading="loadingShifts"
          size="small"
          :pagination="pagination"
          :columns="columns"
          :data-source="associateShifts"
          :scroll="{ y: 200 }"
          @change="handleTableChange"
        >
          <span slot="date" slot-scope="text, record">
            {{ formatDate(record.scheduled_start) }} -
            {{ formatDate(record.scheduled_end) }}
          </span>
          <span slot="status" slot-scope="text, record">
            <shift-status :shift="record" />
          </span>
          <template slot="footer">
            <span v-if="hasSelected">{{ `Selected ${form.shift_ids.length} shifts` }}</span>
          </template>
        </a-table></template
      >
    </a-form-model>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Close</a-button>
        <div class="flex justify-content-end">
          <a-tooltip v-if="hasEmptyProfiles">
            <template slot="title"> The associate does not have any other profiles to move shifts to. </template>
            <a-button @click="submit" :disabled="hasEmptyProfiles || loading" :loading="loading" data-testid="move-shifts-submit-button" icon="export" type="primary" ghost> Move </a-button>
          </a-tooltip>
          <a-button v-else @click="submit" :disabled="hasEmptyProfiles || loading" :loading="loading" data-testid="move-shifts-submit-button" icon="export" type="primary" ghost>
            Move
          </a-button>
        </div>
      </div>
    </template>
  </a-modal>
</template>

<script>
import SearchAssociates from '@/components/search-associates.vue'
import CustomSelect from '@/components/ui/custom-select.vue'
import { SHIFT_STATUS } from '@/store/modules/shifts/constants'
import { deepCopy } from '@/utils/deep-copy'
import { Button, DatePicker, FormModel, Modal, notification, Select, Table, Tooltip } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import ShiftTableStatus from '../shift-table-status.vue'
import { getAssociateStatus } from '@/store/modules/profile/utils'

const { mapActions: shiftActions } = createNamespacedHelpers('shifts')
const { mapActions: profileActions } = createNamespacedHelpers('profile')

const initialForm = {
  holder_id: undefined,
  assignee_id: undefined,
  shift_ids: []
}

const filtersBaseState = Object.freeze({
  shift_statuses: 'completed',
  end_date: undefined,
  start_date: undefined
})

const initialPagination = {
  pageSizeOptions: ['10', '20', '50', '100'],
  showSizeChanger: true,
  pageSize: 10,
  current: 1,
  total: 0
}

export default {
  components: {
    'a-modal': Modal,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-button': Button,
    'a-tooltip': Tooltip,
    'a-table': Table,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-range-picker': DatePicker.RangePicker,
    'custom-select': CustomSelect,
    'search-associates': SearchAssociates,
    'shift-status': ShiftTableStatus
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    initialValues: {
      type: Object,
      default: undefined
    }
  },
  emits: ['move'],
  data() {
    return {
      filters: { ...filtersBaseState },

      shiftStatuses: Object.entries(SHIFT_STATUS)
        .map(([key, value]) => ({
          key,
          ...value
        }))
        .filter(item => ['scheduled', 'completed'].includes(item.key)),

      loadingShifts: false,
      loadingProfiles: false,
      loading: false,

      associateShifts: [],
      profiles: [],

      form: deepCopy(initialForm),

      pagination: initialPagination,
      columns: [
        {
          title: 'Shift #',
          dataIndex: 'id'
        },
        {
          title: 'Date',
          scopedSlots: { customRender: 'date' }
        },
        {
          title: 'Status',
          scopedSlots: { customRender: 'status' }
        }
      ],

      rules: {
        holder_id: [
          {
            required: true,
            message: 'Please select associate',
            trigger: 'change'
          }
        ],
        assignee_id: [
          {
            required: true,
            message: 'Please select associate',
            trigger: 'change'
          }
        ],
        shift_ids: [
          {
            required: true,
            message: 'Please select shifts',
            trigger: 'change',
            type: 'array'
          }
        ]
      },

      requestKey: `shifts-table-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    }
  },
  watch: {
    filters: {
      async handler() {
        this.pagination = deepCopy(initialPagination)
        await this.fetchShifts()
      },
      deep: true
    },
    'form.holder_id': {
      async handler(newVal) {
        this.form.shift_ids = []
        this.filters = deepCopy(filtersBaseState)
        this.form.assignee_id = undefined

        if (newVal && this.visible) {
          await this.fetchAssociateProfiles(newVal)
          await this.fetchShifts()
        }
      },
      immediate: true
    },
    visible: {
      async handler(newVal) {
        if (newVal && this.form.holder_id) {
          await this.fetchAssociateProfiles(this.form.holder_id)
          await this.fetchShifts()
        }
      }
    },
    initialValues: {
      handler(newVal) {
        if (!newVal) {
          return
        }

        if (newVal.associateNumber) {
          this.form.holder_id = newVal.associateNumber
        }
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    isStaff() {
      return this.$store.state.applicationState.currentProject.staffing
    },

    hasSelected() {
      return this.form.shift_ids.length > 0
    },
    hasEmptyProfiles() {
      return this.profiles.length === 0
    },
    dateRange() {
      return {
        Today: [moment(), moment()],
        'This Month': [moment().startOf('month'), moment().endOf('month')]
      }
    },

    dateProxy: {
      get() {
        return [this.filters.start_date, this.filters.end_date]
      },
      set(value) {
        this.filters.start_date = value[0]
        this.filters.end_date = value[1]
      }
    }
  },
  methods: {
    ...profileActions(['getAssociateProfiles']),
    ...shiftActions(['getShifts', 'postShiftMove']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    getTagStatus(work_status, terminationType) {
      return getAssociateStatus(work_status, terminationType)
    },

    formatDate(date) {
      return moment(date).format(this.$store.state.applicationState.dateFormat)
    },

    closeModal() {
      this.form = deepCopy(initialForm)
      this.associateShifts = []
      this.$emit('update:visible', false)
    },

    onSelectChange(selectedRowKeys) {
      this.form.shift_ids = selectedRowKeys
    },

    handleTableChange(pagination) {
      const isPaginationChanged =
        pagination.current !== this.pagination.current || pagination.pageSize !== this.pagination.pageSize

      if (!isPaginationChanged) {
        return
      }
      this.pagination = { ...pagination }

      this.fetchShifts()
    },

    async fetchShifts() {
      this.loadingShifts = true
      let skipFinally = false

      try {
        const associate = this.profiles.find(item => item.associate_number === this.form.holder_id)

        if (!associate) return

        const payload = {
          filter: {
            shift_type: 'worked',
            associates: [associate.id],
            ...this.filters,
            shift_statuses: [this.filters.shift_statuses]
          },
          page: this.pagination.current,
          size: this.pagination.pageSize,
          fields: '{id,status,scheduled_start,scheduled_end,external_payment_info,internal_payment_info,pay_code}',
          requestGroup: this.requestKey
        }

        const { items, total_count } = await this.getShifts(payload)

        this.associateShifts = items
        this.pagination.total = total_count
      } catch (error) {
        if (error === 'canceled') {
          skipFinally = true
          this.loadingShifts = true
          return
        }
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        if (!skipFinally) this.loadingShifts = false
      }
    },

    async fetchAssociateProfiles(associate_number) {
      if (!associate_number) {
        return
      }

      this.loadingProfiles = true

      try {
        const payload = {
          associate_number,
          with_disabled: true
        }

        const { associates } = await this.getAssociateProfiles(payload)

        this.profiles = associates
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingProfiles = false
      }
    },

    async submit() {
      try {
        const valid = await new Promise(resolve => {
          this.$refs.formRef.validate(resolve)
        })

        if (!valid) {
          this.showNotification('warning', 'Validation Error', 'Please fill in all required fields correctly.')

          return
        }

        this.loading = true

        const shiftIds = [...this.form.shift_ids]

        const payload = {
          shifts: shiftIds,
          new_associate: this.form.assignee_id
        }

        await this.postShiftMove(payload)

        notification.success({
          message: 'Success',
          description: 'Shifts were successfully moved',
          duration: 10,
          btn: h => {
            return h(
              Button,
              {
                props: {
                  type: 'primary',
                  size: 'small'
                },
                on: {
                  click: () => {
                    this.$router
                      .replace({
                        query: {
                          shift_ids: shiftIds
                        }
                      })
                      .catch(() => {})
                  }
                }
              },
              'Open Shifts'
            )
          }
        })

        this.closeModal()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}
</style>
